import { ExternalLink } from "@/components/ui/icons";
import { type KeyboardEvent, type ReactNode, useState } from "react";
import { Linking, Platform, Pressable, ScrollView, Text, View } from "react-native";

import type { components } from "@/api/generated/openapi";
import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { formatMoney } from "@/lib/money/money";
import { cn } from "@/lib/ui/cn";
import { useThemeColors } from "@/theme/theme";

type CardSale = components["schemas"]["CardSaleOut"];
type MisprintObservation = components["schemas"]["MisprintObservationOut"];

export type RecentSale = {
  id: string;
  source: string;
  price: string;
  soldAt: string;
  context: string;
  quantity?: number;
  url?: string | null;
  flag?: CardSale["flag"];
};

const CONDITIONS: Record<string, string> = {
  NM: "Near Mint",
  LP: "Lightly Played",
  MP: "Moderately Played",
  HP: "Heavily Played",
  DMG: "Damaged",
};

export const FLAGS = {
  signed: { label: "SIGNED", className: "bg-warning/15", textClassName: "text-warning" },
  error: { label: "ERROR", className: "bg-warning/15", textClassName: "text-warning" },
  perfect: { label: "GEM", className: "bg-surface-muted", textClassName: "text-subtle" },
};

function listingUrl(value: string): string | undefined {
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) && !url.username && !url.password
      ? value
      : undefined;
  } catch {
    return undefined;
  }
}

/** Merge for display only; existing median, liquidity and Market values keep their own inputs. */
export function cardRecentSales(
  sales: readonly CardSale[],
  misprint?: MisprintObservation | null,
  additional: readonly RecentSale[] = [],
): RecentSale[] {
  const rows: RecentSale[] = sales.map((sale, index) => ({
    id: `${sale.marketplace}:${sale.url}:${index}`,
    source: sale.marketplace,
    price: sale.price,
    soldAt: sale.sold_at,
    context: sale.grade_label ?? "Raw · Condition not recorded",
    url: listingUrl(sale.url),
    flag: sale.flag,
  }));
  // A scope label is required to avoid showing exact sales under an incomplete legacy grade.
  if (misprint?.scope_label) {
    for (const sale of misprint.recent_sales ?? []) {
      rows.push({
        id: `misprint:${sale.sale_id}`,
        source: "Misprint",
        price: sale.price,
        soldAt: sale.sold_at,
        context: CONDITIONS[misprint.scope_label] ?? misprint.scope_label,
        quantity: sale.quantity,
      });
    }
  }
  const seen = new Set(rows.map((row) => row.url ?? row.id));
  for (const sale of additional) {
    const key = sale.url ?? sale.id;
    if (!seen.has(key)) {
      rows.push({ ...sale, url: sale.url ? listingUrl(sale.url) : undefined });
      seen.add(key);
    }
  }
  return rows.sort((a, b) => Date.parse(b.soldAt) - Date.parse(a.soldAt));
}

export type SaleSort = { key: "date" | "price"; direction: "asc" | "desc"; };

/** The sales ordered by sale date or price; equal keys keep their given order. */
export function sortSales(
  sales: readonly RecentSale[],
  { key, direction }: SaleSort,
): RecentSale[] {
  const value = (sale: RecentSale) => key === "date" ? Date.parse(sale.soldAt) : Number(sale.price);
  const sign = direction === "asc" ? 1 : -1;
  return [...sales].sort((a, b) => sign * (value(a) - value(b)));
}

/** The one grade or condition every sale shares, or null when they differ (or there are none). */
export function sharedContext(sales: readonly RecentSale[]): string | null {
  const first = sales[0]?.context;
  return first !== undefined && sales.every((sale) => sale.context === first) ? first : null;
}

export function saleDate(iso: string): string {
  const date = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function SaleRow({ sale, first }: { sale: RecentSale; first: boolean; }) {
  const c = useThemeColors();
  const flag = sale.flag ? FLAGS[sale.flag] : null;
  const date = saleDate(sale.soldAt);
  const label = `Sold ${date} for ${
    formatMoney(sale.price)
  } USD per copy on ${sale.source}, ${sale.context}${
    sale.quantity ? `, ${sale.quantity} ${sale.quantity === 1 ? "copy" : "copies"}` : ""
  }${flag ? ` (${sale.flag})` : ""}`;
  const body = (
    <View
      className={cn(
        "min-h-14 flex-row items-center gap-3 px-4 py-3",
        !first && "border-t border-border",
      )}
      accessible={!sale.url}
      accessibilityLabel={!sale.url ? label : undefined}
    >
      <View className="flex-1 gap-1">
        <View className="flex-row flex-wrap items-center gap-1.5">
          <Text className="text-[13px] font-label text-foreground">{sale.source}</Text>
          {sale.url ? <ExternalLink aria-hidden size={12} color={c.subtle} /> : null}
          {flag ? <Badge {...flag} /> : null}
        </View>
        <Text className="text-[12px] font-body text-muted-foreground">{sale.context}</Text>
        <Text className="text-[12px] font-body text-subtle">{date}</Text>
      </View>
      <View className="items-end gap-1">
        <Text className="font-num-bold text-[15px] text-foreground">{formatMoney(sale.price)}</Text>
        {sale.quantity && sale.quantity > 1
          ? (
            <Text className="text-[12px] font-body text-subtle">
              {`${sale.quantity} copies · each`}
            </Text>
          )
          : null}
      </View>
    </View>
  );
  if (!sale.url) return body;
  const url = sale.url;
  return (
    <Pressable
      {...(Platform.OS === "web"
        ? { href: sale.url, hrefAttrs: { target: "_blank", rel: "noopener noreferrer" } }
        : {})}
      onPress={(event) => {
        if (Platform.OS === "web") event.preventDefault();
        void Linking.openURL(url).catch(() => {});
      }}
      accessibilityRole="link"
      accessibilityLabel={`${label} — open listing`}
      className="active:opacity-70"
    >
      {body}
    </Pressable>
  );
}

/** The marketplace filter above the sales: "All" and one tab per source, with sale counts. */
export function SourceTabs({ sales, sources, selected, onSelect }: {
  sales: readonly RecentSale[];
  sources: readonly string[];
  selected: string | null;
  onSelect: (source: string | null) => void;
}) {
  return (
    <ScrollView horizontal className="grow-0" showsHorizontalScrollIndicator={false}>
      <View
        accessibilityRole="tablist"
        accessibilityLabel="Recent sales sources"
        className="flex-row gap-1"
      >
        {[null, ...sources].map((value) => {
          const label = value ?? "All";
          const count = value
            ? sales.filter((sale) => sale.source === value).length
            : sales.length;
          const active = selected === value;
          return (
            <Pressable
              key={label}
              accessibilityRole="tab"
              accessibilityLabel={`${label}, ${count} sales`}
              accessibilityState={{ selected: active }}
              aria-selected={active}
              {...(Platform.OS === "web"
                ? ({
                  tabIndex: active ? 0 : -1,
                  onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
                    const tabs = Array.from(
                      event.currentTarget.parentElement?.querySelectorAll<HTMLElement>(
                        '[role="tab"]',
                      ) ?? [],
                    );
                    const index = tabs.indexOf(event.currentTarget);
                    const next = event.key === "ArrowRight"
                      ? (index + 1) % tabs.length
                      : event.key === "ArrowLeft"
                      ? (index + tabs.length - 1) % tabs.length
                      : event.key === "Home"
                      ? 0
                      : event.key === "End"
                      ? tabs.length - 1
                      : null;
                    if (next !== null) {
                      event.preventDefault();
                      tabs[next]?.focus();
                      tabs[next]?.click();
                    }
                  },
                } as object)
                : {})}
              onPress={() => onSelect(value)}
              className={cn(
                "min-h-12 justify-center border-b-2 px-3",
                active ? "border-primary" : "border-transparent",
              )}
            >
              <Text
                className={cn(
                  "text-sm font-label",
                  active ? "text-primary" : "text-muted-foreground",
                )}
              >
                {label} <Text className="font-num text-xs">{count}</Text>
              </Text>
            </Pressable>
          );
        })}
      </View>
    </ScrollView>
  );
}

/** Recent sales with local marketplace filtering and original sale attribution. */
export function RecentSales(
  { sales, median, emptyMessage, capturedAt, refreshFailed, action }: {
    sales: RecentSale[];
    median?: { price: string; count: number; sources: string[]; };
    emptyMessage?: string;
    /** Capture time of additional marketplace observations; never a sale date. */
    capturedAt?: string | null;
    refreshFailed?: boolean;
    /** A link that belongs with the sales, such as the eBay solds search; shown after them. */
    action?: ReactNode;
  },
) {
  const [expanded, setExpanded] = useState(false);
  const [source, setSource] = useState<string | null>(null);
  const sources = [...new Set(sales.map((sale) => sale.source))].sort();
  const selected = source && sources.includes(source) ? source : null;
  const filtered = selected ? sales.filter((sale) => sale.source === selected) : sales;
  const visible = expanded ? filtered : filtered.slice(0, 8);
  const select = (value: string | null) => {
    setSource(value);
    setExpanded(false);
  };
  return (
    <View className="gap-2" testID="recent-sales">
      <View className="flex-row flex-wrap items-baseline justify-between gap-2">
        <Text
          className="text-xs uppercase tracking-widest font-label text-subtle"
          accessibilityRole="header"
          aria-level={2}
        >
          Recent Sales
        </Text>
        <Text className="text-[12px] font-body text-subtle">USD per copy</Text>
      </View>
      {capturedAt && (
        <Text className="text-[12px] font-body text-subtle">
          {`Marketplace sales checked ${saleDate(capturedAt)}`}
        </Text>
      )}
      {refreshFailed && sales.length > 0 && (
        <Text className="text-[12px] font-body text-subtle">
          Showing saved sales while the latest update is unavailable.
        </Text>
      )}
      {median && !selected
        ? (
          <View className="gap-1">
            <View
              className="flex-row flex-wrap items-baseline gap-1.5"
              accessibilityRole="text"
              accessibilityLabel={`Recent median ${
                formatMoney(median.price)
              }, based on ${median.count} sales from ${median.sources.join(", ")}`}
            >
              <Text className="text-[13px] font-label text-muted-foreground">Recent median</Text>
              <Text className="font-num-bold text-lg text-foreground">
                {formatMoney(median.price)}
              </Text>
              <Text className="text-[12px] font-body text-subtle">{`· ${median.count} sales`}</Text>
            </View>
            <Text className="text-[12px] font-body text-subtle">
              {`${median.sources.join(", ")} · 90 days`}
            </Text>
          </View>
        )
        : null}
      {sales.length > 0 && (
        <SourceTabs sales={sales} sources={sources} selected={selected} onSelect={select} />
      )}
      {sales.length === 0
        ? (
          <View className="items-center gap-1 rounded-xl border border-dashed border-border py-8 px-4">
            <Text className="text-sm font-title text-muted-foreground">
              {emptyMessage ?? "No recent sales on record"}
            </Text>
            {!emptyMessage && (
              <Text className="text-[12px] font-body text-subtle">
                No sales have been reported for this scope.
              </Text>
            )}
          </View>
        )
        : (
          <Card className="gap-0 p-0">
            {visible.map((sale, i) => <SaleRow key={sale.id} sale={sale} first={i === 0} />)}
          </Card>
        )}
      {visible.length < filtered.length
        ? (
          <Pressable
            onPress={() => setExpanded(true)}
            accessibilityRole="button"
            accessibilityLabel={`See all ${filtered.length} sales`}
            className="items-center rounded-xl border border-border bg-surface py-3"
          >
            <Text className="text-[13px] font-label text-primary">
              {`See all ${filtered.length} sales`}
            </Text>
          </Pressable>
        )
        : null}
      {action}
    </View>
  );
}
